import {
  APP_INITIALIZER,
  ApplicationConfig,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withPreloading, PreloadAllModules } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';

// --- Imports do PrimeNG ---
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeuix/themes/lara'; 
import { definePreset } from '@primeuix/themes';

// --- Serviços do PrimeNG ---
// ADICIONEI O ConfirmationService AQUI NOS IMPORTS
import { MessageService, ConfirmationService } from 'primeng/api'; 
import { DialogService } from 'primeng/dynamicdialog';

import { routes } from './app.routes';
import { blockUiInterceptor } from './core/interceptors/block-ui.interceptor';
import { tokenInterceptor } from './core/interceptors/token.interceptor';
import { errorResponseInterceptor } from './core/interceptors/error-response.interceptor';
import { AuthService } from './core/services/infrastructure/auth.service';
import { firstValueFrom } from 'rxjs';

registerLocaleData(localePt);

// --- Definição das Cores (Alemidia Blue) usando LARA ---
const AlemidiaBluePreset = definePreset(Lara, {
    semantic: {
        primary: {
            50: '#f0f9ff',
            100: '#e0f2fe',
            200: '#bae6fd',
            300: '#7dd3fc',
            400: '#38bdf8',
            500: '#0085ca', 
            600: '#0284c7',
            700: '#0369a1',
            800: '#075985',
            900: '#0c4a6e',
            950: '#082f49'
        }
    }
});

function initializeApp(): Promise<any> {
  const authService = inject(AuthService);
  return firstValueFrom(authService.checkSessionOnBootstrap());
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes,withPreloading(PreloadAllModules)),
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    
    // --- Serviços Globais do PrimeNG (SINGLETONS) ---
    MessageService,
    DialogService,
    ConfirmationService, // <--- ADICIONEI ESTE PROVIDER. É CRUCIAL PARA O MODAL ABRIR.
    
    provideAppInitializer(initializeApp),
    
    provideHttpClient(
      withInterceptors([blockUiInterceptor, tokenInterceptor, errorResponseInterceptor]),
      withFetch()
    ),
    
    provideAnimations(),
    
    providePrimeNG({
      theme: {
        preset: AlemidiaBluePreset,
        options: {
          darkModeSelector: false, 
        },
      },
      ripple: true,
      translation: {
        // Datas e calendário
        dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
        dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
        dayNamesMin: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
        monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
        monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
        today: 'Hoje',
        clear: 'Limpar',
        dateFormat: 'dd/mm/yy',
        weekHeader: 'Sem',
        firstDayOfWeek: 0,
        // Diálogos de confirmação
        accept: 'Sim',
        reject: 'Não',
        choose: 'Escolher',
        upload: 'Enviar',
        cancel: 'Cancelar',
        // Mensagens vazias
        emptyMessage: 'Nenhum resultado encontrado',
        emptyFilterMessage: 'Nenhum resultado encontrado',
        emptySearchMessage: 'Nenhum resultado para a busca',
        emptySelectionMessage: 'Nenhum item selecionado',
        // Senha
        weak: 'Fraca',
        medium: 'Média',
        strong: 'Forte',
        passwordPrompt: 'Digite uma senha',
        // Filtros de tabela
        addRule: 'Adicionar regra',
        removeRule: 'Remover regra',
        apply: 'Aplicar',
        matchAll: 'Corresponder todos',
        matchAny: 'Corresponder algum',
        contains: 'Contém',
        notContains: 'Não contém',
        startsWith: 'Começa com',
        endsWith: 'Termina com',
        equals: 'Igual a',
        notEquals: 'Diferente de',
        noFilter: 'Sem filtro',
        lt: 'Menor que',
        lte: 'Menor ou igual a',
        gt: 'Maior que',
        gte: 'Maior ou igual a',
        is: 'É',
        isNot: 'Não é',
        before: 'Antes',
        after: 'Depois',
        dateIs: 'Data é',
        dateIsNot: 'Data não é',
        dateBefore: 'Data antes de',
        dateAfter: 'Data depois de',
        // Upload
        fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB'],
        // Acessibilidade (aria)
        aria: {
          trueLabel: 'Sim',
          falseLabel: 'Não',
          nullLabel: 'Não selecionado',
          close: 'Fechar',
          previous: 'Anterior',
          next: 'Próximo',
          navigation: 'Navegação',
          selectAll: 'Selecionar todos',
          unselectAll: 'Desmarcar todos',
          pageLabel: 'Página {page}',
          firstPageLabel: 'Primeira Página',
          lastPageLabel: 'Última Página',
          nextPageLabel: 'Próxima Página',
          prevPageLabel: 'Página Anterior',
          rowsPerPageLabel: 'Linhas por página',
          editRow: 'Editar linha',
          saveEdit: 'Salvar',
          cancelEdit: 'Cancelar',
          zoomImage: 'Ampliar imagem',
          zoomIn: 'Ampliar',
          zoomOut: 'Reduzir',
        },
      },
    }),
  ],
};